<template>
  <!-- FI-1813 public ops-desk submission form. unauth; hydrated from
       /bookings/ops-desk/form/<slug>. mobile-first single column; large
       tap targets; conditional fields render from the server-provided
       schema so the backend stays the source of truth. -->
  <div class="op-public-bg">
    <div class="op-public-card">

      <header class="op-public-header">
        <div class="op-public-eyebrow">Event Response Manager</div>
        <h1 class="op-public-title">Submit an event</h1>
        <div class="op-public-sub" v-if="schema && schema.display_name">
          for <strong>{{ schema.display_name }}</strong>
        </div>
      </header>

      <div v-if="loading" class="op-public-state">
        <i class="fa fa-spinner fa-spin"></i>&nbsp;Loading form...
      </div>

      <div v-else-if="loadError" class="op-public-state op-public-state--error">
        <i class="fa fa-exclamation-triangle"></i>&nbsp;
        We couldn't load this form. Double-check the URL or contact your dispatcher.
      </div>

      <!-- form is intentionally off — the tenant admin disabled public
           access. distinct from loadError so visitors don't think
           something's broken; explains the path forward (auth or ask
           dispatch). -->
      <div v-else-if="disabled" class="op-public-state op-public-state--disabled">
        <i class="fa fa-lock op-public-disabled-icon"></i>
        <h2>Form unavailable</h2>
        <p>
          This form isn't accepting public submissions right now.
          If you're staff, log in to file an entry from the Event Response Manager.
          Otherwise, reach out to your dispatcher directly.
        </p>
      </div>

      <div v-else-if="submitted" class="op-public-success">
        <i class="fa fa-check-circle op-public-success-icon"></i>
        <h2>Thanks, your event was logged.</h2>
        <p v-if="referenceNumber">
          Your reference number is
          <span class="op-public-ref">#{{ referenceNumber }}</span>.
          Keep it handy if you need to follow up.
        </p>
        <p>
          Ops will follow up if there's anything to clarify.
          You can close this page, or submit another.
        </p>
        <b-button variant="outline-primary" @click="reset">
          Submit another
        </b-button>
      </div>

      <form v-else @submit.prevent="onSubmit" class="op-public-form" autocomplete="off">

        <!-- entry type tabs -->
        <div class="op-public-section">
          <label class="op-public-label">What kind of event?</label>
          <div class="op-public-entry-tabs">
            <button
              v-for="et in entryTypes"
              :key="et.value"
              type="button"
              class="op-public-entry-tab"
              :class="{ 'op-public-entry-tab--active': form.entry_type === et.value }"
              @click="selectEntryType(et.value)"
            >
              <i class="fa" :class="et.icon"></i>
              <span>{{ et.label }}</span>
            </button>
          </div>
        </div>

        <!-- category -->
        <div v-if="categoriesForEntryType.length" class="op-public-section">
          <label class="op-public-label" for="op-cat">Category</label>
          <b-form-select
            id="op-cat"
            v-model="form.category"
            :options="categoryOptions"
            required
            class="op-public-input"
          />
        </div>

        <!-- FI-1820 sos surfacing: breakdown/accident are exactly the
             moments a driver may need roadside help, so the sos entry
             point rides inline right under the category pick. -->
        <div v-if="showSosCallout" class="op-public-sos">
          <div class="op-public-sos-text">
            <strong>Vehicle immobilized or need roadside help?</strong>
            You can send an SOS text without leaving this form.
          </div>
          <a class="op-public-sos-btn" :href="sosSmsHref">
            <i class="fa fa-exclamation-triangle"></i>&nbsp;Text SOS to {{ sosPhoneDisplay }}
          </a>
          <div class="op-public-sos-hint">Opens your messaging app with the SOS message ready to send.</div>
        </div>

        <!-- conditional fields per schema -->
        <div v-if="conditionalFields.length" class="op-public-section op-public-section--conditional">
          <div v-for="field in conditionalFields" :key="field.key" class="op-public-field">
            <label :for="'op-f-' + field.key" class="op-public-label">
              {{ field.label }}
              <span v-if="field.required" class="op-public-required">*</span>
            </label>

            <b-form-input
              v-if="field.type === 'text' || field.type === 'tel' || field.type === 'email'"
              :id="'op-f-' + field.key"
              v-model="form.extras[field.key]"
              :type="field.type"
              :placeholder="field.placeholder || ''"
              :required="!!field.required"
              class="op-public-input"
            />
            <b-form-textarea
              v-else-if="field.type === 'textarea'"
              :id="'op-f-' + field.key"
              v-model="form.extras[field.key]"
              :placeholder="field.placeholder || ''"
              :required="!!field.required"
              rows="3"
              class="op-public-input"
            />
            <b-form-input
              v-else-if="field.type === 'number'"
              :id="'op-f-' + field.key"
              v-model.number="form.extras[field.key]"
              type="number"
              :placeholder="field.placeholder || ''"
              :required="!!field.required"
              class="op-public-input"
            />
            <b-form-select
              v-else-if="field.type === 'select'"
              :id="'op-f-' + field.key"
              v-model="form.extras[field.key]"
              :options="selectOptionsFor(field)"
              :required="!!field.required"
              class="op-public-input"
            />
            <b-form-checkbox
              v-else-if="field.type === 'checkbox'"
              :id="'op-f-' + field.key"
              v-model="form.extras[field.key]"
              class="op-public-check"
            >
              {{ field.label }}
            </b-form-checkbox>

            <div v-if="field.help" class="op-public-help">{{ field.help }}</div>
          </div>
        </div>

        <!-- what happened (always-on, primary description) -->
        <div class="op-public-section">
          <label class="op-public-label" for="op-what">What happened? <span class="op-public-required">*</span></label>
          <b-form-textarea
            id="op-what"
            v-model="form.what_happened"
            placeholder="A short description in your own words..."
            required rows="4"
            class="op-public-input"
          />
        </div>

        <!-- optional context fields, collapsed-by-default look but always visible -->
        <div class="op-public-section op-public-section--context">
          <div class="op-public-section-header">Context (optional)</div>
          <div class="op-public-row">
            <div class="op-public-field">
              <label class="op-public-label" for="op-driver">Driver</label>
              <b-form-input id="op-driver" v-model="form.driver_id" class="op-public-input" placeholder="name or ID"/>
            </div>
            <div class="op-public-field">
              <label class="op-public-label" for="op-vehicle">Vehicle #</label>
              <b-form-input id="op-vehicle" v-model="form.vehicle_id" class="op-public-input" placeholder="coach #"/>
            </div>
          </div>
          <div class="op-public-row">
            <div class="op-public-field">
              <label class="op-public-label" for="op-charter">Charter / booking #</label>
              <b-form-input id="op-charter" v-model="form.charter_ref" class="op-public-input"/>
            </div>
            <div class="op-public-field">
              <label class="op-public-label" for="op-loc">Location</label>
              <b-form-input id="op-loc" v-model="form.location_text" class="op-public-input" placeholder="e.g. I-95 N near exit 27"/>
            </div>
          </div>
        </div>

        <!-- photos (FI-1812): staged to s3 while the user keeps typing -->
        <div class="op-public-section op-public-section--photos">
          <div class="op-public-section-header">Photos (optional)</div>
          <od-photo-input
            ref="photoInput"
            :presign="presignPhoto"
            @input="attachmentKeys = $event"
            @busy="photosBusy = $event"
          />
        </div>

        <!-- who's submitting -->
        <div class="op-public-section op-public-section--who">
          <div class="op-public-section-header">About you</div>
          <div class="op-public-field">
            <label class="op-public-label" for="op-name">Your name <span class="op-public-required">*</span></label>
            <b-form-input id="op-name" v-model="form.name" required class="op-public-input"/>
          </div>
          <div class="op-public-row">
            <div class="op-public-field">
              <label class="op-public-label" for="op-email">Email</label>
              <b-form-input id="op-email" v-model="form.email" type="email" class="op-public-input" placeholder="you@example.com"/>
            </div>
            <div class="op-public-field">
              <label class="op-public-label" for="op-phone">Phone</label>
              <b-form-input id="op-phone" v-model="form.phone" type="tel" class="op-public-input" placeholder="(555) 555-1234"/>
            </div>
          </div>
          <div class="op-public-help">Email or phone (at least one) so we can follow up.</div>
        </div>

        <!-- bot trap. label/style intentionally invisible. -->
        <input type="text" v-model="form._honeypot" tabindex="-1" autocomplete="off" class="op-public-honeypot" aria-hidden="true"/>

        <div v-if="submitError" class="op-public-error">
          {{ submitError }}
        </div>

        <div class="op-public-actions">
          <b-button
            type="submit"
            variant="primary"
            size="lg"
            :disabled="submitting || photosBusy || !canSubmit"
            class="op-public-submit"
          >
            <i class="fa fa-paper-plane"></i>&nbsp;
            <span v-if="submitting">Sending...</span>
            <span v-else-if="photosBusy">Uploading photos...</span>
            <span v-else>Submit event</span>
          </b-button>
        </div>
      </form>

      <footer class="op-public-footer">
        Powered by Fleet Integrations Event Response Manager
      </footer>
    </div>
  </div>
</template>

<script>
import {
  get_ops_desk_public_form_schema,
  presign_ops_desk_public_attachment,
  submit_ops_desk_public_form,
} from '../networking/apis'
import OdPhotoInput from './od_photo_input.vue'

const ENTRY_TYPE_META = [
  { value: 'incident', label: 'Incident',  icon: 'fa-exclamation-triangle' },
  { value: 'kudos',    label: 'Kudos',     icon: 'fa-star-o' },
  { value: 'note',     label: 'Note',      icon: 'fa-comment-o' },
]

export default {
  name: 'ops_desk_public_form',

  components: { OdPhotoInput },

  data() {
    return {
      loading: true,
      loadError: false,
      disabled: false,
      schema: null,
      submitting: false,
      submitted: false,
      referenceNumber: null,
      submitError: '',
      attachmentKeys: [],
      photosBusy: false,

      entryTypes: ENTRY_TYPE_META,

      form: {
        entry_type: 'incident',
        category: '',
        name: '',
        email: '',
        phone: '',
        what_happened: '',
        driver_id: '',
        vehicle_id: '',
        charter_ref: '',
        location_text: '',
        _honeypot: '',
        extras: {},
      },
    }
  },

  computed: {
    slug() {
      return this.$route.params.slug
    },
    categoriesForEntryType() {
      if (!this.schema) return []
      return (this.schema.categories && this.schema.categories[this.form.entry_type]) || []
    },
    categoryOptions() {
      const base = [{ value: '', text: 'Pick one...', disabled: true }]
      return base.concat(this.categoriesForEntryType.map(cat => ({
        value: cat,
        text: this.readableLabel(cat),
      })))
    },
    conditionalFields() {
      if (!this.schema || !this.schema.schemas) return []
      const bucket = this.schema.schemas.entry_types[this.form.entry_type] || {}
      // entry types whose schema doesn't vary by category (kudos, note)
      // store fields under _default; fall back to it when the picked
      // category isn't in the bucket.
      const cat = this.form.category
      return bucket[cat] || bucket._default || []
    },
    canSubmit() {
      if (!this.form.name) return false
      if (!this.form.email && !this.form.phone) return false
      if (!this.form.what_happened) return false
      if (this.categoriesForEntryType.length && !this.form.category) return false
      return true
    },
    // FI-1820: the schema endpoint returns sos {phone} only when the
    // tenant has bus sos enabled, so presence is the whole gate.
    showSosCallout() {
      return !!(this.schema && this.schema.sos && this.schema.sos.phone)
        && this.form.entry_type === 'incident'
        && (this.form.category === 'breakdown' || this.form.category === 'accident')
    },
    sosPhoneDisplay() {
      const p = (this.schema && this.schema.sos && this.schema.sos.phone) || ''
      const m = p.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
      return m ? '(' + m[1] + ') ' + m[2] + '-' + m[3] : p
    },
    sosSmsHref() {
      const p = (this.schema && this.schema.sos && this.schema.sos.phone) || ''
      // "?&body=" is the cross-platform spelling (ios wants &, android ?)
      const parts = ['SOS ' + (this.form.vehicle_id || '[vehicle name]')]
      if (this.form.location_text) parts.push(this.form.location_text)
      return 'sms:' + p + '?&body=' + encodeURIComponent(parts.join(', '))
    },
  },

  watch: {
    // seed select extras to '' so the placeholder option displays as
    // selected; an undefined v-model matches no option and the control
    // renders blank.
    conditionalFields(fields) {
      (fields || []).forEach(f => {
        if (f.type === 'select' && this.form.extras[f.key] === undefined) {
          this.$set(this.form.extras, f.key, '')
        }
      })
    },
  },

  mounted() {
    this.loadSchema()
  },

  methods: {
    async loadSchema() {
      this.loading = true
      this.loadError = false
      this.disabled = false
      try {
        const res = await get_ops_desk_public_form_schema(this.slug)
        const data = (res && res.value && res.value.data) || null
        if (data && data.disabled) {
          // tenant admin turned public access off — distinct from a load
          // failure; show the lock state instead of the generic error.
          this.disabled = true
          this.schema = { display_name: data.display_name }
          return
        }
        if (!data || !data.schemas) {
          this.loadError = true
          return
        }
        this.schema = data
        // pick a default category if exactly one available, else leave empty
        const cats = (data.categories && data.categories.incident) || []
        if (cats.length === 1) this.form.category = cats[0]
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk_public_form loadSchema failed', e)
        this.loadError = true
      } finally {
        this.loading = false
      }
    },

    selectEntryType(et) {
      this.form.entry_type = et
      this.form.category = ''
      // reset extras when switching entry types so a kudos_target from
      // a prior kudos draft doesn't leak into an incident submission
      this.form.extras = {}
    },

    selectOptionsFor(field) {
      const opts = field.options || []
      return [
        { value: '', text: 'Pick one...', disabled: field.required },
        ...opts.map(o => ({ value: o.value, text: o.label })),
      ]
    },

    readableLabel(v) {
      if (!v) return ''
      return String(v).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    },

    reset() {
      this.submitted = false
      this.referenceNumber = null
      this.submitError = ''
      this.attachmentKeys = []
      if (this.$refs.photoInput) this.$refs.photoInput.clear()
      this.form = {
        entry_type: 'incident',
        category: '',
        name: this.form.name,
        email: this.form.email,
        phone: this.form.phone,
        what_happened: '',
        driver_id: '',
        vehicle_id: '',
        charter_ref: '',
        location_text: '',
        _honeypot: '',
        extras: {},
      }
    },

    async presignPhoto(file) {
      const res = await presign_ops_desk_public_attachment(this.slug, {
        filename: file.name,
        content_type: file.type,
        byte_size: file.size,
      })
      return (res && res.value && res.value.data) || null
    },

    async onSubmit() {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.submitError = ''
      try {
        const fd = new FormData()
        fd.append('entry_type', this.form.entry_type)
        fd.append('category', this.form.category || '')
        fd.append('name', this.form.name || '')
        fd.append('email', this.form.email || '')
        fd.append('phone', this.form.phone || '')
        fd.append('what_happened', this.form.what_happened || '')
        if (this.form.driver_id) fd.append('driver_id', this.form.driver_id)
        if (this.form.vehicle_id) fd.append('vehicle_id', this.form.vehicle_id)
        if (this.form.charter_ref) fd.append('charter_ref', this.form.charter_ref)
        if (this.form.location_text) fd.append('location_text', this.form.location_text)
        fd.append('_honeypot', this.form._honeypot || '')
        // staged photo keys; the backend finalizes them onto the event
        // (metadata strip + move out of staging)
        if (this.attachmentKeys.length) {
          fd.append('attachment_keys', JSON.stringify(this.attachmentKeys))
        }
        // schema-driven extras land as plain form fields; the backend
        // whitelists them per the schema for this (entry_type, category).
        Object.entries(this.form.extras || {}).forEach(([k, v]) => {
          if (v === '' || v === null || v === undefined) return
          fd.append(k, typeof v === 'boolean' ? (v ? '1' : '') : String(v))
        })
        const res = await submit_ops_desk_public_form(this.slug, fd)
        // surface the allocated event number as a reference the submitter
        // can quote in a follow-up. the envelope is {value:{data:{event_id}}};
        // a honeypot hit returns a null id, so guard before showing it.
        const eid = res && res.value && res.value.data && res.value.data.event_id
        this.referenceNumber = (eid || eid === 0) ? eid : null
        this.submitted = true
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ops_desk_public_form submit failed', e)
        // api errors are a flat {message} body; keep the error_data
        // path as a fallback for wrapped shapes.
        const body = e && e.response && e.response.data
        const msg = body && (body.message
          || (body.error_data && (body.error_data.error_message || body.error_data.message)))
        this.submitError = msg || 'We could not submit your event. Try again, or contact your dispatcher.'
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
/* deliberately distinct from the auth'd FI surface: friendly, mobile-first,
   single column. the page is going to be opened on phones by drivers, by
   customers, and by random visitors via a vanity url — so it should feel
   like a customer-facing form, not an internal tool. */
.op-public-bg {
  min-height: 100vh;
  background: linear-gradient(180deg, #eef4fb 0%, #f7fafd 100%);
  padding: 32px 12px 60px 12px;
}
.op-public-card {
  max-width: 560px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(31, 85, 130, 0.10);
  padding: 28px 24px;
}

.op-public-header { text-align: center; margin-bottom: 18px; }
.op-public-eyebrow {
  font-size: 11px; font-weight: 700; letter-spacing: 1.4px;
  text-transform: uppercase; color: var(--fi-rail-accent);
}
.op-public-title { margin: 4px 0 6px 0; font-size: 24px; font-weight: 600; color: #1d1d1f; }
.op-public-sub { color: #4b5563; font-size: 14px; }

.op-public-state { text-align: center; padding: 32px 12px; color: #6b7280; font-size: 14px; }
.op-public-state--error { color: #b91c1c; }
.op-public-state--disabled { color: #4b5563; padding: 48px 16px; }
.op-public-state--disabled h2 { font-size: 20px; color: #1d1d1f; margin: 12px 0 8px 0; }
.op-public-state--disabled p { max-width: 320px; margin: 0 auto; line-height: 1.5; font-size: 13.5px; }
.op-public-disabled-icon { font-size: 36px; color: #9ca3af; }

.op-public-success { text-align: center; padding: 32px 12px; }
.op-public-success-icon { color: #16a34a; font-size: 40px; }
.op-public-success h2 { margin: 12px 0 6px 0; font-size: 20px; color: #1d1d1f; }
.op-public-success p { color: #4b5563; font-size: 14px; margin-bottom: 18px; }
.op-public-ref {
  display: inline-block;
  font-weight: 700;
  color: #1f5582;
  background: #eaf4fc;
  border: 1px solid #cfe3f5;
  border-radius: 6px;
  padding: 1px 8px;
}

.op-public-form { margin-top: 6px; }
.op-public-section { margin-bottom: 18px; }
.op-public-section--conditional {
  background: #f8fafc; border: 1px solid #e1e8f0;
  border-left: 3px solid var(--fi-rail-accent);
  border-radius: 6px; padding: 12px 14px;
}
.op-public-section--context, .op-public-section--who {
  border-top: 1px solid #eef0f3; padding-top: 14px;
}
.op-public-section-header {
  font-size: 12px; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px;
}
.op-public-label {
  display: block; margin: 4px 0 4px 0;
  font-size: 13px; font-weight: 600; color: #1d1d1f;
}
.op-public-required { color: #dc2626; margin-left: 2px; }
.op-public-input { font-size: 15px; }   /* 15px keeps ios from zooming on focus */
.op-public-check { font-size: 14px; }
.op-public-help { font-size: 12px; color: #6b7280; margin-top: 4px; }
.op-public-error {
  background: #fef2f2; color: #991b1b;
  border: 1px solid #fecaca; border-radius: 6px;
  padding: 10px 12px; font-size: 13.5px;
  margin: 8px 0 14px 0;
}

.op-public-row {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
}
.op-public-field { min-width: 0; }

/* entry type tabs: three big cards in a row */
.op-public-entry-tabs {
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px;
}
.op-public-entry-tab {
  background: #ffffff; border: 1.5px solid #d6dde6;
  border-radius: 8px; padding: 12px 6px;
  font-size: 13px; font-weight: 600; color: #4b5563;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  cursor: pointer; transition: all 0.12s ease;
}
.op-public-entry-tab i { font-size: 20px; color: #6b7280; }
.op-public-entry-tab:hover { border-color: var(--fi-rail-accent); color: #1f5582; }
.op-public-entry-tab--active {
  border-color: var(--fi-rail-accent); background: #eaf4fc; color: #1f5582;
}
.op-public-entry-tab--active i { color: var(--fi-rail-accent); }

.op-public-actions { margin-top: 22px; }
.op-public-submit { width: 100%; }

.op-public-footer {
  margin-top: 22px; padding-top: 14px;
  border-top: 1px solid #eef0f3;
  text-align: center; font-size: 11.5px; color: #9ca3af;
}

/* FI-1820 sos callout: shown when the picked category suggests the
   vehicle may be disabled. amber, not red — urgent but not alarming. */
.op-public-sos {
  margin-top: 14px; padding: 12px 14px;
  border: 1px solid #f0d9a8; border-radius: 8px;
  background: #fdf6e7;
}
.op-public-sos-text { font-size: 13px; color: #6b5518; }
.op-public-sos-text strong { display: block; color: #8a6d1a; }
.op-public-sos-btn {
  display: inline-block; margin-top: 9px; padding: 8px 14px;
  border-radius: 6px; background: #b7791f; color: #fff !important;
  font-size: 13px; font-weight: 600; text-decoration: none !important;
}
.op-public-sos-btn:hover { background: #9c6410; }
.op-public-sos-hint { margin-top: 7px; font-size: 11.5px; color: #9b8749; }

/* honeypot: visually hidden but not display:none (bots ignore display:none) */
.op-public-honeypot {
  position: absolute; left: -10000px; top: auto;
  width: 1px; height: 1px; overflow: hidden;
}

@media (max-width: 480px) {
  .op-public-card { padding: 20px 14px; }
  .op-public-row { grid-template-columns: 1fr; }
  .op-public-entry-tabs { grid-template-columns: 1fr; }
}
</style>
